import { useEffect, useState } from "react";

import { MarkdownNotes } from "@/components/cinch/MarkdownNotes/MarkdownNotes";

import { useUpsertNote, type NoteKind } from "../api";

/**
 * Auto-saving markdown notes for a cash-flow run (summary-level) or a single
 * week (variance reasons). Owns the value + persistence — debounces a save
 * 800ms after the user stops typing — and delegates the editing UX to the
 * shared {@link MarkdownNotes} component. Notes land in the ``cash_flow``
 * schema so they're queryable inputs to future forecast analysis.
 */
export function CashFlowNotesArea({
  runId,
  weekEnding,
  kind,
  initialBody,
  label,
  placeholder,
  readOnly = false,
  minHeightPx = 80,
}: {
  runId: string;
  /** null → summary-level note; a date string → that week's variance note. */
  weekEnding: string | null;
  kind: NoteKind;
  initialBody: string;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  minHeightPx?: number;
}) {
  const [body, setBody] = useState(initialBody);
  const mutation = useUpsertNote();

  useEffect(() => {
    setBody(initialBody);
  }, [initialBody]);

  useEffect(() => {
    if (readOnly || body === initialBody) return;
    const t = setTimeout(() => {
      mutation.mutate({ run_id: runId, week_ending: weekEnding, kind, body });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, initialBody, runId, weekEnding, kind, readOnly]);

  return (
    <MarkdownNotes
      value={body}
      onChange={setBody}
      label={label}
      placeholder={placeholder}
      readOnly={readOnly}
      minHeightPx={minHeightPx}
      statusText={mutation.isPending ? "Saving…" : "Markdown"}
    />
  );
}
